import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
} from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

export type Status =
  | { kind: 'loading' }
  | { kind: 'invalid-callback'; reason: string }
  | { kind: 'missing-state' }
  | { kind: 'no-token' }
  | { kind: 'send-failed'; reason: string }
  | { kind: 'done' };

/** The outcome of checking the query string, before any token is read. */
export type CliAuthRequest =
  | { kind: 'ready'; callback: string; state: string }
  | { kind: 'invalid-callback'; reason: string }
  | { kind: 'missing-state' };

const ALLOWED_HOSTS = new Set(['127.0.0.1', 'localhost']);

export const isCallbackUrlAllowed = (raw: string | null): boolean => {
  if (!raw) return false;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'http:') return false;
  if (!ALLOWED_HOSTS.has(parsed.hostname)) return false;
  return true;
};

interface MinimalIdentityApi {
  getCredentials(): Promise<{ token?: string }>;
}

interface DeliverTokenToCliOptions {
  search: string;
  identityApi: MinimalIdentityApi;
  fetchImpl?: typeof fetch;
}

/**
 * Checks the query string without touching the identity token.
 *
 * Split out of {@link deliverTokenToCli} so the page can show the user what is
 * about to be sent, and to whom, *before* anything is read or delivered.
 */
export const readCliAuthRequest = (search: string): CliAuthRequest => {
  const params = new URLSearchParams(search);
  const callback = params.get('callback');
  const state = params.get('state');

  if (!isCallbackUrlAllowed(callback)) {
    return {
      kind: 'invalid-callback',
      reason:
        'The callback URL must be a loopback address (http://127.0.0.1 or http://localhost).',
    };
  }
  if (!state) {
    return { kind: 'missing-state' };
  }
  return { kind: 'ready', callback: callback as string, state };
};

export const deliverTokenToCli = async ({
  search,
  identityApi,
  fetchImpl,
}: DeliverTokenToCliOptions): Promise<Status> => {
  const request = readCliAuthRequest(search);
  if (request.kind !== 'ready') {
    return request;
  }
  const { callback, state } = request;

  const errMessage = (err: unknown): string =>
    err instanceof Error ? err.message : String(err);

  let token: string | undefined;
  try {
    const creds = await identityApi.getCredentials();
    token = creds.token;
  } catch (err) {
    return { kind: 'send-failed', reason: errMessage(err) };
  }

  if (!token) {
    return { kind: 'no-token' };
  }

  const doFetch = fetchImpl ?? globalThis.fetch;
  try {
    const response = await doFetch(callback, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, state }),
    });
    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      return {
        kind: 'send-failed',
        reason: `loopback returned ${response.status} ${response.statusText}${
          detail ? `: ${detail}` : ''
        }`,
      };
    }
    return { kind: 'done' };
  } catch (err) {
    return { kind: 'send-failed', reason: errMessage(err) };
  }
};

interface CliAuthPageProps {
  fetchImpl?: typeof fetch;
}

export const CliAuthPage = ({ fetchImpl }: CliAuthPageProps) => {
  const location = useLocation();
  const identityApi = useApi(identityApiRef);
  const request = useMemo(
    () => readCliAuthRequest(location.search),
    [location.search],
  );
  const [status, setStatus] = useState<Status | undefined>();

  // Deliberately NOT done on mount. `state` is echoed back from the query
  // string, so it is bound to nothing this browser generated and gives no CSRF
  // protection: any page can navigate the user here and a local listener would
  // capture a full Backstage session token. The user approves the handover, and
  // sees the `state` the CLI printed so they can match the two.
  const approve = useCallback(async () => {
    setStatus({ kind: 'loading' });
    setStatus(
      await deliverTokenToCli({
        search: location.search,
        identityApi,
        fetchImpl,
      }),
    );
  }, [location.search, identityApi, fetchImpl]);

  return (
    <Page themeId="tool">
      <Header
        title="Frontstage CLI sign-in"
        subtitle="Hand the session token back to the local CLI"
      />
      <Content>
        <InfoCard title="CLI authentication">
          {request.kind === 'invalid-callback' && (
            <Typography color="error">
              Refused to send the token: {request.reason}
            </Typography>
          )}
          {request.kind === 'missing-state' && (
            <Typography color="error">
              Refused to send the token: the request is missing a state
              parameter.
            </Typography>
          )}
          {request.kind === 'ready' && !status && (
            <>
              <Typography sx={{ mb: 2 }}>
                The Frontstage CLI is asking for your session token. Only
                continue if you started this sign-in yourself, and only if the
                code below matches the one the CLI printed.
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Verification code: <code>{request.state}</code>
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Destination: <code>{request.callback}</code>
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={approve}
                data-testid="cli-auth-approve"
              >
                Send the token to the CLI
              </Button>
            </>
          )}
          {status?.kind === 'loading' && (
            <>
              <Progress />
              <Typography>Signing you in to the CLI…</Typography>
            </>
          )}
          {status?.kind === 'no-token' && (
            <Typography color="error">
              Could not obtain an identity token. Sign in and try again.
            </Typography>
          )}
          {status?.kind === 'send-failed' && (
            <Typography color="error">
              Failed to deliver the token to the CLI: {status.reason}
            </Typography>
          )}
          {status?.kind === 'done' && (
            <Typography>You can close this tab.</Typography>
          )}
        </InfoCard>
      </Content>
    </Page>
  );
};
